// The Rewards half of the banner — t-159,
// .codelane/artifacts/t-159/leaderboard-pending-rewards.html (934-1002).
//
// One package, two tabs. Settings expands it into the field diff and collapses
// the prize table to a single "12 rows → 14 rows" line; here it is the other
// way round — the board's fields shrink to one row with a link, and the prize
// table is spelled out range by range. The banner, the counts and the actions
// around it are the same component on both.

import { AlertTriangle, X } from "lucide-react";

import {
  bandLabel,
  bandWeight,
  type CoverageGroup,
  type RankCoverage,
} from "./rewardCoverage";
import {
  rewardsCountLabel,
  summariseRewardsDiff,
  type RewardDiffRow,
  type RewardsTableDiff,
} from "./rewardsDiff";
import type { DiffRow } from "./pendingDiff";
import styles from "./pending.module.css";

/** Small counts read as words in this banner's voice, and there are never
   more than five of them: WAS_CREATE_ONLY holds exactly five fields. */
const WORDS = ["no", "one", "two", "three", "four", "five"];

function spell(n: number): string {
  return WORDS[n] ?? String(n);
}

const TAG_CLASS: Record<RewardDiffRow["change"], string> = {
  added: styles.rdAdd,
  removed: styles.rdDel,
  changed: styles.rdChg,
};

const TAG_LABEL: Record<RewardDiffRow["change"], string> = {
  added: "added",
  removed: "removed",
  changed: "changed",
};

// --- The board's fields, collapsed ------------------------------------------

export interface BoardFieldsGroupProps {
  /** Every field row of the package — the whole Settings-side diff, which is
     drawn there and only counted here. */
  rows: DiffRow[];
  /** How many of them used to be settable only when the board was created. */
  reopened: number;
  /** Where the Settings tab is. A real anchor: the tabs of this page are a
     query parameter, so the link works without a router in the tree. */
  settingsHref: string;
}

/**
 * Ten fields as one line. The operator on this tab came for the prize table,
 * and a diff that opened with ten rows about the window and the segmentation
 * would bury it — so the fields say how many they are, where they are shown
 * in full, and nothing else.
 */
export function BoardFieldsGroup({
  rows,
  reopened,
  settingsHref,
}: BoardFieldsGroupProps) {
  return (
    <>
      <div className={styles.diffGroup}>
        Board settings
        <span className={styles.countBadge}>{rows.length}</span>
        <span className={`${styles.diffNote} ${styles.groupNote}`}>
          <a className={styles.tabLink} href={settingsHref}>
            Open the Settings tab
          </a>
        </span>
      </div>
      <div className={styles.diffRow}>
        <div>
          <div className={styles.diffName}>
            {rows.map((r) => r.api).join(", ")}
          </div>
          <div className={styles.diffApi}>leaderboards</div>
        </div>
        <div className={styles.diffVals}>
          <span className={styles.diffNote}>
            {rows.length} {rows.length === 1 ? "field" : "fields"} — shown in
            full on the Settings tab
            {reopened > 0 && (
              <>
                , {spell(reopened)} of them impossible to change after creation
                until now
              </>
            )}
          </span>
        </div>
        <span />
      </div>
    </>
  );
}

// --- Rank coverage ----------------------------------------------------------

export interface CoverageStripProps {
  /** One strip per ranking the table describes — a tier board restarts at
     rank 1 inside every league, and a member_contribution board carries two
     tables at once. See rankCoverageGroups. */
  groups: CoverageGroup[];
  /** Whether the table being replaced loses a percentile row — the one
     explanation of a gap this page can actually derive. */
  percentileRemoved: boolean;
}

/**
 * Which ranks the replacement pays. A warning and never a refusal: a board is
 * allowed to pay nothing to a band, so the strip says where the empty bands
 * are and the scheduling button stays exactly as it was.
 */
export function CoverageStrip({ groups, percentileRemoved }: CoverageStripProps) {
  return (
    <div className={styles.coverage}>
      <div className={styles.covTitle}>Rank coverage after the replacement</div>
      {groups.map((group) => (
        <div key={group.key} className={styles.covGroup}>
          {/* Named only where there is more than one ranking to tell apart —
             on an ordinary board the heading would name the only strip on the
             page. */}
          {group.label && (
            <div className={styles.covGroupLabel}>{group.label}</div>
          )}
          <CoverageBars
            coverage={group.coverage}
            percentileRemoved={percentileRemoved}
          />
        </div>
      ))}
    </div>
  );
}

/** One ranking's bar, its scale and the sentence about its holes. */
function CoverageBars({
  coverage,
  percentileRemoved,
}: {
  coverage: RankCoverage;
  percentileRemoved: boolean;
}) {
  return (
    <>
      {coverage.drawable ? (
        <>
          <div className={styles.covBar} aria-hidden>
            {coverage.bands.map((band) => (
              <div
                key={`${band.from}-${band.to}`}
                className={band.covered ? styles.cov : styles.covGap}
                style={{ flex: bandWeight(band) }}
              />
            ))}
          </div>
          <div className={styles.covScale}>
            {coverage.scale.map((mark, i) => (
              <span key={`${mark}-${i}`}>{mark}</span>
            ))}
          </div>
          {coverage.gaps.length > 0 && (
            <div className={styles.covNote}>
              <AlertTriangle size={14} aria-hidden />
              <span>
                <b>
                  {coverage.gaps.map(bandLabel).join(" and ")}{" "}
                  {coverage.gaps.length === 1 &&
                  coverage.gaps[0].from === coverage.gaps[0].to
                    ? "pays"
                    : "pay"}{" "}
                  nothing.
                </b>{" "}
                {percentileRemoved
                  ? "The percentile row that used to reach them is being removed and no rank range replaces it. "
                  : "No row of the replacement covers that band. "}
                Scheduling is allowed; this is a warning, not a refusal.
              </span>
            </div>
          )}
        </>
      ) : (
        <div className={styles.diffNote}>
          {coverage.percentileRows > 0
            ? "The replacement ranks by percentile, so it has no fixed band of ranks to draw — which share of a cycle a row pays is decided when that cycle closes."
            : "The replacement pays no rank ranges, so there is no band of ranks to draw."}
        </div>
      )}
    </>
  );
}

// --- The prize table, row by row --------------------------------------------

export interface RewardsDiffGroupProps {
  table: RewardsTableDiff;
  coverage: CoverageGroup[];
  /** Take the prize table out of the package and leave the rest of it
     waiting. Absent on a package that is not waiting any more. */
  onCancel?: () => void;
  cancelBusy?: boolean;
}

export function RewardsDiffGroup({
  table,
  coverage,
  onCancel,
  cancelBusy,
}: RewardsDiffGroupProps) {
  const percentileRemoved = table.rows.some(
    (r) => r.change === "removed" && r.range.endsWith("%"),
  );
  return (
    <>
      <div className={styles.diffGroup}>
        Prize table
        <span className={styles.countBadge}>{rewardsCountLabel(table)}</span>
        <span className={`${styles.diffNote} ${styles.groupNote}`}>
          {summariseRewardsDiff(table)}
        </span>
        {onCancel && (
          <button
            type="button"
            className={styles.diffDel}
            disabled={cancelBusy}
            aria-label="Remove the prize table from the package"
            onClick={onCancel}
          >
            <X size={13} aria-hidden />
          </button>
        )}
      </div>
      <div className={`${styles.rewardDiffRow} ${styles.rdHead}`}>
        <div>Change</div>
        <div>Range</div>
        <div>Payload</div>
        <div>Note</div>
      </div>
      {table.rows.map((row) => (
        <div
          key={`${row.change}:${row.key}`}
          className={`${styles.rewardDiffRow} ${
            row.change === "removed" ? styles.rdRemoved : ""
          }`}
        >
          <div>
            <span className={`${styles.rdTag} ${TAG_CLASS[row.change]}`}>
              {TAG_LABEL[row.change]}
            </span>
          </div>
          <div className={styles.rdMono}>{row.range}</div>
          {row.change === "changed" ? (
            <div className={styles.diffVals}>
              <span className={styles.diffOld}>{row.oldPayload}</span>
              <span className={styles.diffArrow} aria-hidden>
                →
              </span>
              <span className={styles.diffNew}>{row.newPayload}</span>
            </div>
          ) : (
            <div className={styles.rdMono}>
              {row.newPayload ?? row.oldPayload}
            </div>
          )}
          <div className={styles.diffNote}>{row.note}</div>
        </div>
      ))}
      <div className={`${styles.rewardDiffRow} ${styles.covRow}`}>
        <CoverageStrip groups={coverage} percentileRemoved={percentileRemoved} />
      </div>
    </>
  );
}

// --- The two halves together ------------------------------------------------

export interface PendingRewardsDiffProps
  extends Omit<BoardFieldsGroupProps, "rows">,
    RewardsDiffGroupProps {
  boardRows: DiffRow[];
}

/** What the banner draws on this tab: the board's fields in one line, then the
   prize table spelled out. Either half may be missing — a package can carry
   only one of the two. */
export function PendingRewardsDiff({
  boardRows,
  reopened,
  settingsHref,
  table,
  coverage,
  onCancel,
  cancelBusy,
}: PendingRewardsDiffProps) {
  return (
    <div className={styles.diffBox}>
      {boardRows.length > 0 && (
        <BoardFieldsGroup
          rows={boardRows}
          reopened={reopened}
          settingsHref={settingsHref}
        />
      )}
      <RewardsDiffGroup
        table={table}
        coverage={coverage}
        onCancel={onCancel}
        cancelBusy={cancelBusy}
      />
    </div>
  );
}
